import React, { useEffect, useRef, useState } from "react";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useTheme } from "@mui/material/styles";
import Badge from "@components/Badge/Badge";
import { getBadgeType } from "@components/DataTable/columns";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import { STEPPER_TYPE } from "@components/StepperTabs/StepperTabs.types";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type {
  DryBulkNominationResponse,
  DryBulkResource,
} from "@services/dry-bulk-nomination/types";
import {
  approveDryBulkNomination,
  cancelDryBulkNomination,
  claimDryBulkNominationTask,
  DRY_BULK_NOMINATION_QUERY_KEY,
  getDryBulkNomination,
  getDryBulkNominationVesselHeader,
  rejectDryBulkNomination,
  replaceDryBulkOperationsResources,
  unclaimDryBulkNominationTask,
} from "@services/dryBulkNominationApi";
import type { VesselNominationHeaderResponse } from "@services/vessel-nomination/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddCargoOperationFromNomination from "./components/AddCargoOperationFromNomination";
import DecisionDetailsDrawer from "./components/DecisionDetailsDrawer";
import DecisionDrawer, { type DecisionSubmit } from "./components/DecisionDrawer";
import DryBulkDocumentsTab from "./components/DryBulkDocumentsTab";
import {
  ActivitiesSection,
  CargoLinesSection,
  ResourcesSection,
} from "./components/DryBulkNominationSections";
import DryBulkNominationSummary from "./components/DryBulkNominationSummary";
import DryBulkReviewClaimPanel from "./components/DryBulkReviewClaimPanel";
import DryBulkVesselDetails from "./components/DryBulkVesselDetails";
import ShortNoticeBanner from "./components/ShortNoticeBanner";
import DryBulkButton from "./components/ui/DryBulkButton";
import {
  emptyDryBulkNominationForm,
  type DryBulkNominationFormData,
  type ResourceFormData,
} from "./schema/dryBulkNominationSchema";
import {
  fromDryBulkNominationResponse,
  getFieldErrors,
  getHttpStatus,
  toDryBulkResource,
} from "./utils/dryBulkNominationMapping";
import { formatUaeDateTime } from "./utils/formatUaeDate";
import { isShortNoticeCall } from "./utils/isShortNoticeCall";

const LANG_BASE = "vessel-nomination.dry-bulk.add";
const LANG_REVIEW = "vessel-nomination.dry-bulk.review";
const LANG_DETAILS = "vessel-nomination.dry-bulk.details";
const DECISION_POLL_ATTEMPTS = 10;
const DECISION_POLL_INTERVAL_MS = 1000;
const TEST_ID_BASE = "dry-bulk-add-vessel-nomination";
const TELEMETRY_MODULE = "dry_bulk_vessel_nomination";
const TELEMETRY_SCREEN = "DryBulkNominationDetail";
const TELEMETRY_ENTITY_TYPE = "dry_bulk_nomination";
const transactionContext = (action: string, entityId?: string) => ({
  module: TELEMETRY_MODULE,
  screen: TELEMETRY_SCREEN,
  action,
  entityType: TELEMETRY_ENTITY_TYPE,
  ...(entityId ? { entityId } : {}),
});
const GRID_PATH = `/${paths.vesselNomination.index}`;
const DRY_BULK_PATH = `${GRID_PATH}/dry-bulk`;

const pollWhileStatus = async (
  id: string,
  waitingStatus: string,
  onFresh: (fresh: DryBulkNominationResponse) => unknown,
) => {
  for (let attempt = 0; attempt < DECISION_POLL_ATTEMPTS; attempt += 1) {
    const fresh = await getDryBulkNomination(id);
    onFresh(fresh);
    if (fresh.status !== waitingStatus) return;
    await new Promise((resolve) => setTimeout(resolve, DECISION_POLL_INTERVAL_MS));
  }
};

const DryBulkNominationDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: nominationId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { userActionPermissions, userAccessPermissions, user } = useAuthStore();
  const [loadedAt] = useState(() => Date.now());
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [decisionMode, setDecisionMode] = useState<"approve" | "reject" | null>(null);
  const [decisionErrors, setDecisionErrors] = useState<Record<string, string>>({});
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const canRequest = checkPermission(userActionPermissions, "canRequestDryBulkVesselNomination");
  const canRead = checkPermission(userAccessPermissions, "hasDryBulkVesselNominationAccess");
  const isReviewer = !canRequest && canRead;
  const queryKey = [DRY_BULK_NOMINATION_QUERY_KEY, nominationId];

  const draftQuery = useQuery<DryBulkNominationResponse>({
    queryKey,
    queryFn: () => getDryBulkNomination(nominationId as string),
    enabled: (canRequest || canRead) && !!nominationId,
  });
  const nomination = draftQuery.data;

  const {
    data: header,
    isSuccess: isHeaderSuccess,
    isError: isHeaderError,
  } = useQuery<VesselNominationHeaderResponse>({
    queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, nominationId, "vessel-header"],
    queryFn: () => getDryBulkNominationVesselHeader(nominationId as string),
    enabled:
      (canRequest || canRead) && !!nominationId && !!nomination && nomination.status !== "DRAFT",
  });
  const isHeaderSettled = isHeaderSuccess || isHeaderError;

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);
  useEffect(() => {
    if (
      hasTrackedInitialLoad.current ||
      !nomination ||
      nomination.status === "DRAFT" ||
      !isHeaderSettled
    ) {
      return;
    }
    hasTrackedInitialLoad.current = true;
    trackScreenLoad("Vessel Call Management", "Dry Bulk Nomination Details", startedAt.current);
  }, [nomination, isHeaderSettled]);

  const form = useForm<DryBulkNominationFormData>({ defaultValues: emptyDryBulkNominationForm() });
  useEffect(() => {
    if (nomination && nomination.status !== "DRAFT") {
      form.reset(fromDryBulkNominationResponse(nomination));
    }
  }, [nomination, form]);

  useEffect(() => {
    if (!canRequest && !canRead) {
      navigate(GRID_PATH, { replace: true });
    }
  }, [canRequest, canRead, navigate]);

  useEffect(() => {
    if (draftQuery.isError) {
      notify({
        type: "error",
        title: t(`${LANG_BASE}.load-error-title`),
        description: t(`${LANG_BASE}.load-error-description`),
      });
      navigate(GRID_PATH, { replace: true });
    }
  }, [draftQuery.isError, navigate, notify, t]);

  useEffect(() => {
    if (nomination?.status === "DRAFT") {
      navigate(`${DRY_BULK_PATH}/add/${nominationId}`, { replace: true });
    }
  }, [nomination?.status, nominationId, navigate]);

  const replaceNomination = (updated: DryBulkNominationResponse) =>
    queryClient.setQueryData(queryKey, updated);

  const showActionError = (error: unknown, titleKey: string) => {
    const status = getHttpStatus(error);
    const detail = (error as { response?: { data?: { detail?: string } } } | null)?.response?.data
      ?.detail;
    const expected = status === 409 || status === 403;
    notify({
      type: "error",
      title: t(`${LANG_REVIEW}.${status === 403 ? "forbidden-title" : titleKey}`),
      description: expected && detail ? detail : t(`${LANG_REVIEW}.error-description`),
    });
  };

  const claim = useMutation({
    mutationFn: () =>
      runTransaction(
        transactionContext("claim_dry_bulk_nomination", nominationId),
        (transactionId) => claimDryBulkNominationTask(nominationId as string, { transactionId }),
      ),
    onSuccess: replaceNomination,
    onError: (error) => showActionError(error, "claim-error-title"),
  });

  const unclaim = useMutation({
    mutationFn: () =>
      runTransaction(
        transactionContext("unclaim_dry_bulk_nomination", nominationId),
        (transactionId) => unclaimDryBulkNominationTask(nominationId as string, { transactionId }),
      ),
    onSuccess: replaceNomination,
    onError: (error) => showActionError(error, "unclaim-error-title"),
  });

  const settleDecision = () =>
    pollWhileStatus(nominationId as string, "PENDING_APPROVAL", replaceNomination);

  const resubmitted = (location?.state as { resubmitted?: boolean } | null | undefined)
    ?.resubmitted;
  const resubmitPolled = useRef(false);
  const currentStatus = nomination?.status;
  useEffect(() => {
    if (!resubmitted || !nominationId || currentStatus !== "REJECTED" || resubmitPolled.current) {
      return;
    }
    resubmitPolled.current = true;
    void pollWhileStatus(nominationId, "REJECTED", (fresh) =>
      queryClient.setQueryData([DRY_BULK_NOMINATION_QUERY_KEY, nominationId], fresh),
    );
  }, [resubmitted, nominationId, currentStatus, queryClient]);

  const decisionSucceeded = (toastKey: string) => {
    setDecisionMode(null);
    setDecisionErrors({});
    notify({ type: "success", title: t(`${LANG_REVIEW}.${toastKey}`), description: "" });
    void settleDecision();
  };

  const decisionFailed = (error: unknown) => {
    if (getHttpStatus(error) === 400) {
      setDecisionErrors(getFieldErrors(error));
      return;
    }
    showActionError(error, "decision-error-title");
  };

  const approve = useMutation({
    mutationFn: (values: DecisionSubmit) =>
      runTransaction(
        transactionContext("approve_dry_bulk_nomination", nominationId),
        (transactionId) =>
          approveDryBulkNomination(
            nominationId as string,
            {
              expectedBerthCode: values.expectedBerthCode as string,
              comments: values.text || undefined,
              attachments: values.attachments,
            },
            { transactionId },
          ),
      ),
    onSuccess: () => decisionSucceeded("approved-toast"),
    onError: decisionFailed,
  });

  const reject = useMutation({
    mutationFn: (values: DecisionSubmit) =>
      runTransaction(
        transactionContext("reject_dry_bulk_nomination", nominationId),
        (transactionId) =>
          rejectDryBulkNomination(
            nominationId as string,
            {
              reason: values.text,
              attachments: values.attachments,
            },
            { transactionId },
          ),
      ),
    onSuccess: () => decisionSucceeded("rejected-toast"),
    onError: decisionFailed,
  });

  const replaceResources = useMutation({
    mutationFn: (next: ResourceFormData[]) =>
      runTransaction(
        transactionContext("update_dry_bulk_nomination_operations_resources", nominationId),
        (transactionId) =>
          replaceDryBulkOperationsResources(
            nominationId as string,
            next
              .filter((resource) => resource.owner === "OPERATIONS_DEPARTMENT")
              .map(toDryBulkResource) as DryBulkResource[],
            { transactionId },
          ),
      ),
    onSuccess: replaceNomination,
    onError: (error) => showActionError(error, "resources-error-title"),
  });

  const cancel = useMutation({
    mutationFn: () =>
      runTransaction(
        transactionContext("cancel_dry_bulk_nomination", nominationId),
        (transactionId) => cancelDryBulkNomination(nominationId as string, { transactionId }),
      ),
    onSuccess: (cancelled) => {
      setIsCancelOpen(false);
      replaceNomination(cancelled);
      notify({ type: "success", title: t(`${LANG_DETAILS}.cancelled-toast`), description: "" });
    },
    onError: (error) => {
      setIsCancelOpen(false);
      showActionError(error, "cancel-error-title");
    },
  });

  if ((!canRequest && !canRead) || !nomination || nomination.status === "DRAFT") return null;

  const task = nomination.nominationTask;
  const pending = nomination.status === "PENDING_APPROVAL";
  const holdsClaim =
    isReviewer &&
    task?.status === "CLAIMED" &&
    !!user?.preferredUsername &&
    task.claimOwnerName === user.preferredUsername;
  const canDecide = holdsClaim && pending;
  const agentCanChange = canRequest && pending && nomination.underReview !== true;
  const agentCanUpdate = agentCanChange || (canRequest && nomination.status === "REJECTED");
  const latestDecision = nomination.decisions?.[0];
  const cancelledBy = nomination.status === "CANCELLED" ? latestDecision : undefined;
  const statusLabel = t(`${LANG_DETAILS}.status-labels.${nomination.status}`, {
    defaultValue: nomination.status,
  });
  const vesselTitle =
    header?.vesselImoNumber && header?.vesselName
      ? `${header.vesselImoNumber} - ${header.vesselName}`
      : t(`${LANG_DETAILS}.title`, { reference: nomination.refOrderNumber });
  const showShortNotice =
    isHeaderSuccess && isShortNoticeCall(header?.activeVesselCall?.eta, loadedAt);
  const busy =
    claim.isPending ||
    unclaim.isPending ||
    approve.isPending ||
    reject.isPending ||
    cancel.isPending;

  return (
    <PageLayout dir={dir}>
      <Subheader
        title={vesselTitle}
        subtitle={
          <span>
            <button
              onClick={() => navigate(GRID_PATH)}
              className="cursor-pointer bg-transparent border-0 p-0"
              style={{ color: theme.palette.text.brand }}
            >
              {t("navigation.vessel-nomination")}
            </button>{" "}
            / {vesselTitle}
          </span>
        }
      >
        <div
          className="flex items-center justify-between gap-6"
          data-testid={`${TEST_ID_BASE}-actions`}
        >
          <div className="flex items-center gap-4">
            <Badge
              type={getBadgeType(statusLabel)}
              label={statusLabel}
              testId={`${TEST_ID_BASE}-status`}
            />
            {latestDecision && latestDecision.decision !== "CANCELLED" && (
              <DryBulkButton
                type="button"
                variant="text"
                color="primary"
                testId={`${TEST_ID_BASE}-view-details`}
                label={t(`${LANG_REVIEW}.view-details`)}
                onClick={() => setIsDetailsOpen(true)}
              />
            )}
            {cancelledBy && (
              <span className="text-sm" data-testid={`${TEST_ID_BASE}-cancelled-by`}>
                {t(`${LANG_DETAILS}.cancelled-by`, {
                  name: cancelledBy.decidedByName || cancelledBy.decidedBy,
                  date: formatUaeDateTime(cancelledBy.decidedAt),
                })}
              </span>
            )}
            {isReviewer && task?.status === "CLAIMED" && (
              <span className="text-sm font-medium" data-testid={`${TEST_ID_BASE}-claim-state`}>
                {holdsClaim
                  ? t(`${LANG_REVIEW}.claimed-by-you`)
                  : t(`${LANG_REVIEW}.claimed-by`, { owner: task.claimOwnerName })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {isReviewer && (
              <DryBulkReviewClaimPanel
                testId={TEST_ID_BASE}
                task={task}
                currentUser={user?.preferredUsername}
                busy={busy}
                canDecide={canDecide}
                onClaim={() => claim.mutate()}
                onUnclaim={() => unclaim.mutate()}
                onApprove={() => setDecisionMode("approve")}
                onReject={() => setDecisionMode("reject")}
              />
            )}
            <AddCargoOperationFromNomination
              testId={`${TEST_ID_BASE}-add-cargo-operation`}
              langBase={LANG_DETAILS}
              status={nomination.status}
              vesselCallId={nomination.vesselCallId}
              vesselImo={header?.vesselImoNumber}
            />
            {agentCanChange && (
              <DryBulkButton
                type="button"
                variant="outlined"
                color="primary"
                testId={`${TEST_ID_BASE}-cancel-nomination`}
                startIcon={<CancelOutlined />}
                label={t(`${LANG_DETAILS}.cancel`)}
                onClick={() => setIsCancelOpen(true)}
                disabled={busy}
              />
            )}
            {agentCanUpdate && (
              <DryBulkButton
                type="button"
                variant="outlined"
                color="primary"
                testId={`${TEST_ID_BASE}-update`}
                startIcon={<EditOutlined />}
                label={t(`${LANG_DETAILS}.update-button`)}
                onClick={() => navigate(`${DRY_BULK_PATH}/edit/${nominationId}`)}
                disabled={busy}
              />
            )}
          </div>
        </div>
      </Subheader>
      <div className="pt-8 pb-8 px-24 flex flex-col gap-6" data-testid={TEST_ID_BASE}>
        {showShortNotice && <ShortNoticeBanner testId={TEST_ID_BASE} />}
        <DryBulkVesselDetails
          testId={TEST_ID_BASE}
          header={isHeaderSuccess ? header : undefined}
          agencyCompanyId={nomination.companyId}
        />
        <StepperTabs
          testId={`${TEST_ID_BASE}-tabs`}
          stepperType={STEPPER_TYPE.HORIZONTAL}
          isTypeChangeable={false}
        >
          <StepperTabPanel label={t(`${LANG_DETAILS}.tabs.nomination-details`)}>
            <FormProvider {...form}>
              <div className="flex flex-col gap-6" data-testid={`${TEST_ID_BASE}-details-tab`}>
                <DryBulkNominationSummary
                  testId={`${TEST_ID_BASE}-summary`}
                  nomination={nomination}
                />
                <CargoLinesSection testId={TEST_ID_BASE} readOnly />
                <ActivitiesSection testId={TEST_ID_BASE} readOnly />
                <ResourcesSection
                  testId={TEST_ID_BASE}
                  owner="OPERATIONS_DEPARTMENT"
                  readOnly={!canDecide}
                  controlled={
                    canDecide
                      ? {
                          items: (nomination.resources ?? []) as ResourceFormData[],
                          onChange: (next) => replaceResources.mutate(next),
                          busy: replaceResources.isPending,
                        }
                      : undefined
                  }
                />
                <ResourcesSection testId={TEST_ID_BASE} owner="VESSEL" readOnly />
              </div>
            </FormProvider>
          </StepperTabPanel>
          <StepperTabPanel label={t(`${LANG_DETAILS}.tabs.documents`)}>
            <DryBulkDocumentsTab
              testId={`${TEST_ID_BASE}-documents-tab`}
              attachments={nomination.attachments ?? []}
              decisions={nomination.decisions ?? []}
            />
          </StepperTabPanel>
        </StepperTabs>
      </div>
      {isReviewer && nominationId && (
        <DecisionDrawer
          mode={decisionMode ?? "approve"}
          nominationId={nominationId}
          isOpen={decisionMode !== null}
          isSubmitting={approve.isPending || reject.isPending}
          fieldErrors={decisionErrors}
          preselectedBerthCode={nomination.expectedBerthCode}
          onClose={() => {
            setDecisionMode(null);
            setDecisionErrors({});
          }}
          onSubmit={(values) =>
            decisionMode === "reject" ? reject.mutate(values) : approve.mutate(values)
          }
        />
      )}
      {latestDecision && latestDecision.decision !== "CANCELLED" && (
        <DecisionDetailsDrawer
          decision={latestDecision}
          isOpen={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
        />
      )}
      <GenericDialog
        open={isCancelOpen}
        testId={`${TEST_ID_BASE}-cancel-dialog`}
        title={t(`${LANG_DETAILS}.cancel-dialog.title`)}
        elementContent={t(`${LANG_DETAILS}.cancel-dialog.description`)}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t(`${LANG_DETAILS}.cancel-dialog.confirm`)}
        handleCloseDialog={() => setIsCancelOpen(false)}
        confirmAction={() => cancel.mutate()}
        isSubmitting={cancel.isPending}
      />
    </PageLayout>
  );
};

export default DryBulkNominationDetailsPage;
